import { Megaphone, Send, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/ui/stat-card";
import { ChartCard } from "@/components/ui/chart-card";
import { ChartEmptyState } from "@/components/ui/chart-empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { KpiPeriod } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { useOrgCampaigns } from "../hooks/useOrgCampaigns";
import {
  CAMPAIGN_STATUS_LABELS,
  META_ERROR_LABELS,
  TEMPLATE_CATEGORY_LABELS,
  formatRatio,
  labelOf
} from "../org-kpis.meta";
import { BreakdownBars } from "./BreakdownBars";

interface CampaignsTabProps {
  organizationId: string;
  period: KpiPeriod;
}

export function CampaignsTab({ organizationId, period }: CampaignsTabProps) {
  const { data, isLoading, error, refetch } = useOrgCampaigns(organizationId, period);

  if (isLoading || error || !data) {
    return (
      <LoadingState
        isLoading={isLoading}
        error={error}
        loadingText="Loading campaigns..."
        errorTitle="Failed to load campaigns"
        onRetry={() => refetch()}
      />
    );
  }

  if (data.campaigns === 0) {
    return (
      <ChartEmptyState
        icon={Megaphone}
        title="No campaigns in this period"
        description="Campaigns created in the selected period appear here."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Campaigns"
          value={formatters.formatNumber(data.campaigns)}
          hint={data.byStatus
            .map((s) => `${s.count} ${labelOf(CAMPAIGN_STATUS_LABELS, s.status).toLowerCase()}`)
            .join(", ")}
          icon={Megaphone}
        />
        <StatCard
          label="Recipients"
          value={formatters.formatNumber(data.recipients)}
          icon={Users}
        />
        <StatCard
          label="Delivered"
          value={formatRatio(data.deliveryRate)}
          hint={`${formatRatio(data.readRate)} of delivered were read`}
          icon={Send}
          tone="primary"
        />
        <StatCard
          label="Failed"
          value={formatters.formatNumber(data.failed)}
          tone={data.failed > 0 ? "warning" : "default"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="Recipients by template category"
          description="Utility templates cost a fraction of marketing ones"
        >
          <BreakdownBars
            rows={data.byCategory.map((c) => ({
              label: `${labelOf(TEMPLATE_CATEGORY_LABELS, c.category)} · ${c.campaigns} campaigns`,
              value: c.recipients
            }))}
            format={formatters.formatNumber}
          />
        </ChartCard>
        <ChartCard title="Why sends failed">
          <BreakdownBars
            rows={data.topFailures.map((f) => ({
              label:
                f.code === null
                  ? f.message || "No error code"
                  : `${f.code} · ${META_ERROR_LABELS[f.code] ?? f.message ?? "Other Meta error"}`,
              value: f.count
            }))}
            format={formatters.formatNumber}
            emptyText="No failed sends."
          />
        </ChartCard>
      </div>

      <ChartCard title="Latest campaigns">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 font-medium">Campaign</th>
                <th className="py-2 font-medium">Category</th>
                <th className="py-2 text-right font-medium">Recipients</th>
                <th className="py-2 text-right font-medium">Delivered</th>
                <th className="py-2 text-right font-medium">Read</th>
                <th className="py-2 text-right font-medium">Failed</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((c) => {
                const reached = c.delivered + c.read;
                return (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatters.formatDate(c.createdAt)} ·{" "}
                        {labelOf(CAMPAIGN_STATUS_LABELS, c.status)}
                      </div>
                    </td>
                    <td className="py-2">
                      {c.category ? (
                        <Badge variant="outline">
                          {labelOf(TEMPLATE_CATEGORY_LABELS, c.category)}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatters.formatNumber(c.recipients)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatRatio(c.recipients ? reached / c.recipients : null)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatRatio(reached ? c.read / reached : null)}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatters.formatNumber(c.failed)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
